import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReactGA from 'react-ga4';
import { REACT_APP_DOCS_URL } from '../../constants/config';

// Utility function to convert JSX to HTML string for structured data
const jsxToHtml = (jsx) => {
  // This is a simplified converter - in a real app you might want to use
  // a more robust solution like react-dom/server's renderToString
  const convertElement = (element) => {
    if (typeof element === 'string') return element;
    if (typeof element === 'number') return String(element);
    if (!element) return '';

    if (Array.isArray(element)) {
      return element.map(convertElement).join('');
    }

    if (element.type === 'span' && element.props['data-british']) {
      return `<span data-british="${element.props['data-british']}">${convertElement(element.props.children)}</span>`;
    }

    if (element.type === Link) {
      const href = element.props.to;
      const text = convertElement(element.props.children);
      return `<a href="${href}" class="text-indigo-600 underline">${text}</a>`;
    }

    if (element.props && element.props.children) {
      return convertElement(element.props.children);
    }

    return '';
  };

  return convertElement(jsx);
};

// Helper function to get HTML content for structured data
const getAnswerHtml = (item) => {
  // If answerHtml is provided, use it as an override
  if (item.answerHtml) {
    return item.answerHtml;
  }
  // Otherwise, convert JSX to HTML
  return jsxToHtml(item.answer);
};

const faqs = [
  {
    id: 'ai-take-my-job',
    question: 'Is Sophare AI going to take my job?',
    answer: (
      <>
        Just the boring parts: cleaning data, spreadsheet analysis, and{' '}
        <span data-british="optimising">optimizing</span> your calculations. We give you back the time to focus on
        strategic compensation decisions and shaping the future of work. Let humans do what humans do best.
      </>
    ),
  },
  {
    id: 'eu-pay-transparency-directive',
    question: 'What is the EU Pay Transparency Directive and when does it apply?',
    answer: (
      <>
        It is an EU law adopted in 2023 that requires employers to measure and report gender pay gaps by 2026. Some
        obligations start earlier for job ads and pay ranges (
        <Link
          to={`${REACT_APP_DOCS_URL}/news/eu-pay-transparency-directive-10-months-away`}
          className="text-indigo-600 underline"
        >
          EU Pay Transparency Directive overview
        </Link>
        ).
      </>
    ),
  },
  {
    id: 'supported-regulations',
    question: 'Which countries and regulations does Sophare AI support today?',
    answer: (
      <>
        We support about 50 countries and jurisdictions. Sophare supports the EU Pay Transparency Directive, the UK
        gender pay gap regime, Australia&apos;s WGEA reporting, and state and city rules in the United States (
        <Link
          to={`${REACT_APP_DOCS_URL}/news/us-europe-global-pay-transparency-laws-employee-rights`}
          className="text-indigo-600 underline"
        >
          pay transparency laws overview
        </Link>
        ).
      </>
    ),
    answerHtml:
      'We support about 50 countries and jurisdictions! Sophare supports the ' +
      "EU Pay Transparency Directive, the UK gender pay gap regime, Australia's " +
      'WGEA reporting, and state and city rules in the United States ' +
      '(<a href="' +
      REACT_APP_DOCS_URL +
      '/news/us-europe-global-pay-transparency-laws-employee-rights">' +
      'pay transparency laws overview</a>).',
  },
  {
    id: 'adjusted-unadjusted',
    question: 'How does Sophare AI calculate adjusted vs. unadjusted pay gaps?',
    answer: (
      <>
        Unadjusted gaps compare average pay by gender. Adjusted gaps use statistical models that control for factors
        like role, level, and location (
        <Link
          to={`${REACT_APP_DOCS_URL}/news/adjusted-pay-gap-eu-pay-transparency-directive`}
          className="text-indigo-600 underline"
        >
          method
        </Link>
        ).
      </>
    ),
  },
  {
    id: 'implementation-time',
    question: 'How long does implementation take?',
    answer: (
      <>
        Most teams go live in one to four weeks depending on data readiness. We pride ourselves on our speed of
        implementation and our ability to get you live in a matter of days.
      </>
    ),
  },
  {
    id: 'dry-run',
    question: 'Can we do a dry run before the 2026 EU Pay Transparency Directive deadline?',
    answer: (
      <>Yes. Many customers do dry runs to identify data issues and remediation steps ahead of mandatory reporting.</>
    ),
  },
  {
    id: 'data-sources',
    question: 'What data sources and integrations are supported?',
    answer: (
      <>
        We support any HRIS, payroll, and/or ATS systems. We <span data-british="customise">customize</span> mappings
        for your specific data sources and formats.
      </>
    ),
  },
  {
    id: 'privacy',
    question: 'How is employee privacy protected?',
    answer: (
      <>
        We treat privacy and trust as core to the product itself. Your employees&apos; data is safeguarded at every
        step. We apply role-based access, audit logs, and minimization. Processing aligns with GDPR and customer DPAs (
        <Link to={`${REACT_APP_DOCS_URL}/legal/privacy-policy`} className="text-indigo-600 underline">
          privacy policy
        </Link>
        ).
      </>
    ),
  },
  {
    id: 'pricing',
    question: 'How much does Sophare cost?',
    answer: (
      <>
        Pricing is tiered by employee count and scope, with options for one-off reports or global pay equity modeling.
        We are transparent about our pricing and our customers love the flexibility of our pricing model. (
        <Link to="/pricing" className="text-indigo-600 underline">
          pricing
        </Link>
        ).
      </>
    ),
    answerHtml:
      'Pricing is tiered by employee count and scope, with options for ' +
      'one-off reports or subscriptions. Contact sales for a quote ' +
      '(<a href="/pricing">pricing</a>).',
  },
  {
    id: 'employee-portal',
    question: 'Does Sophare include an employee portal for pay information requests?',
    answer: (
      <>
        Yes. An HR admin can track and respond to requests from employees. We also offer the most customizable views, so
        you can provide exactly the level of transparency your organization chooses (
        <Link
          to={`${REACT_APP_DOCS_URL}/news/eu-pay-transparency-directive-employee-rights`}
          className="text-indigo-600 underline"
        >
          employee right to information details
        </Link>
        ).
      </>
    ),
    answerHtml: 'Yes. Employees can submit requests and admins can respond with ' + 'tracked SLAs.',
  },
  {
    id: 'data-cleaning',
    question: 'What if I actually like cleaning data and making spreadsheets?',
    answer: (
      <>
        That&apos;s a rare talent, and we respect it. You might even help us teach our AI a trick or two. But think
        about all that free time you could have...
      </>
    ),
  },
];

const faqLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: getAnswerHtml(item),
    },
  })),
};

const HomepageFAQ = () => {
  const [openItems, setOpenItems] = useState(new Set());
  const detailsRefs = useRef({});

  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.replace('#', ''));
      if (el) {
        el.open = true;
        setOpenItems((prev) => new Set(prev).add(window.location.hash.replace('#', '')));
      }
    }
  }, []);

  const handleToggle = (itemId, isOpen) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);
      if (isOpen) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  };

  const handleExpandCollapseAll = () => {
    const allOpen = faqs.every((item) => openItems.has(item.id));
    if (allOpen) {
      // Collapse all
      setOpenItems(new Set());
      faqs.forEach((item) => {
        if (detailsRefs.current[item.id]) {
          detailsRefs.current[item.id].open = false;
        }
      });
    } else {
      // Expand all
      const allIds = new Set(faqs.map((item) => item.id));
      setOpenItems(allIds);
      faqs.forEach((item) => {
        if (detailsRefs.current[item.id]) {
          detailsRefs.current[item.id].open = true;
        }
      });
    }
  };

  return (
    <section aria-labelledby="faq-heading" className="faq w-full bg-white py-20 border-t">
      <div className="content max-w-[1120px] mx-auto">
        <div className="flex items-center justify-between gap-4">
          <h2 id="faq-heading" className="text-4xl sm:text-6xl font-bold tracking-tighter">
            FAQ
          </h2>
          <button
            onClick={handleExpandCollapseAll}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors rounded-md hover:bg-gray-50"
            aria-label={
              faqs.every((item) => openItems.has(item.id)) ? 'Collapse all FAQ items' : 'Expand all FAQ items'
            }
          >
            {faqs.every((item) => openItems.has(item.id)) ? (
              <>
                <ExpandLessIcon fontSize="small" />
                <span>Collapse all</span>
              </>
            ) : (
              <>
                <ExpandMoreIcon fontSize="small" />
                <span>Expand all</span>
              </>
            )}
          </button>
        </div>
        <div className="mt-8 divide-y divide-gray-200">
          {faqs.map((item) => (
            <details
              key={item.id}
              id={item.id}
              ref={(el) => (detailsRefs.current[item.id] = el)}
              onToggle={(e) => {
                const isOpen = e.currentTarget.open;
                handleToggle(item.id, isOpen);
                if (isOpen) {
                  ReactGA.event('faq_open', {
                    question_id: item.id,
                    question_text: item.question,
                  });
                }
              }}
              className="group py-4"
            >
              <summary className="cursor-pointer text-lg font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 flex items-center justify-between">
                <span className="flex-1">
                  {item.question}
                  <a
                    href={`#${item.id}`}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Link to this question"
                    className="ml-2 text-indigo-600 opacity-0 group-hover:opacity-100"
                  >
                    <LinkIcon fontSize="small" />
                  </a>
                </span>
                <div className="ml-4 flex-shrink-0">
                  <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                    <svg
                      className={`w-4 h-4 text-indigo-600 transition-transform duration-200 ${
                        openItems.has(item.id) ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </summary>
              <div className="mt-2 text-gray-700">
                <p>{item.answer}</p>
              </div>
            </details>
          ))}
        </div>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </div>
    </section>
  );
};

export default HomepageFAQ;
